import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { PageCard } from 'src/common/PageCard';
import { Poppins } from 'src/common/Poppins';
import Button from 'src/components/form/Button';
import { InfoCircleSvg, SparkleSvg } from 'src/assets/dynamicSvg';

const Card = styled(PageCard)`
  padding: 0;
  border-radius: 8px;
  overflow: hidden;
`;

const Top = styled.div`
  background: ${({ theme }) => theme.colors.cultured};
  border-bottom: 1px solid ${({ theme }) => theme.colors.stroke};
  padding: 16px;
`;

const TextArea = styled.textarea`
  display: block;
  width: 100%;
  border: none;
  outline: none;
  resize: none;
  padding: 0;
  background: transparent;
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.prussianBlue};
  caret-color: ${({ theme }) => theme.colors.gp1};
  min-height: 92px;

  &::placeholder {
    color: ${({ theme }) => theme.colors.cflowerBlue};
  }
  &:disabled {
    background: transparent;
  }
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
`;

const HelperPill = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  border-radius: 8px;
  padding: 8px 16px;
`;

const Count = styled.div<{ $atLimit: boolean }>`
  flex-shrink: 0;
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  color: ${({ theme, $atLimit }) => ($atLimit ? theme.colors.statusRed : theme.colors.cflowerBlue)};
`;

const Bottom = styled.div`
  background: ${({ theme }) => theme.colors.white};
  padding: 16px;
`;

const AnalyseButton = styled(Button).attrs({ primary: true })`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-transform: none;
`;

const MAX_INPUT = 2000;
const MAX_HEIGHT = 272; // ~10 lines at 16px / line-height 1.7; content beyond that scrolls

interface RiskDescriptionCardProps {
  value: string;
  onChange: (value: string) => void;
  onAnalyse: () => void;
  analysing: boolean;
  disabled: boolean;
}

export const RiskDescriptionCard: React.FC<RiskDescriptionCardProps> = ({
  value,
  onChange,
  onAnalyse,
  analysing,
  disabled,
}) => {
  const taRef = useRef<HTMLTextAreaElement>(null);

  // Grow with content up to ~10 lines, then scroll. Re-runs on every value change (incl. "use this suggestion").
  useEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_HEIGHT)}px`;
    el.style.overflowY = el.scrollHeight > MAX_HEIGHT ? 'auto' : 'hidden';
  }, [value]);

  const canAnalyse = value.trim().length > 0 && !analysing && !disabled;
  const atLimit = value.length >= MAX_INPUT;

  return (
    <Card>
      <Top>
        <TextArea
          ref={taRef}
          rows={3}
          disabled={disabled}
          value={value}
          maxLength={MAX_INPUT}
          placeholder="  Explain the risk in your own words. The more details you share, the better we can analyse."
          onChange={(e) => onChange(e.target.value)}
          data-cy="ai-risk-description"
        />
        <Meta>
          <HelperPill>
            <InfoCircleSvg size={14} style={{ flexShrink: 0 }} />
            <Poppins px={14} color="davysGrey" css="line-height: 1.7;">
              Descriptions with a source, event, and consequence helps you get the best result
            </Poppins>
          </HelperPill>
          <Count $atLimit={atLimit} data-cy="ai-description-count">
            {value.length}/{MAX_INPUT}
          </Count>
        </Meta>
      </Top>
      <Bottom>
        <AnalyseButton
          type="button"
          disabled={!canAnalyse}
          loading={analysing}
          onClick={onAnalyse}
          data-cy="ai-analyse"
        >
          <SparkleSvg size={14} color="#FFFFFF" />
          ANALYSE SCENARIO
        </AnalyseButton>
      </Bottom>
    </Card>
  );
};
